import {
  NavigationProp,
  RouteProp,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { format } from "date-fns";
import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { useFragment } from "react-relay";

import {
  facilityToScheduleOption,
  ScheduleOption,
} from "@/components/ScheduleSwitcher";
import { ScheduleSwitcher } from "@/components/ScheduleSwitcher/ScheduleSwitcher";
import { useScheduleNavigator } from "@/hooks/useScheduleNavigator";
import { CourseNote } from "@whoosh/design-system/src/apps/PatronApp/screens/Agenda/CourseNote";
import { VStack } from "@whoosh/design-system/src/components/primitive";

import { AgendaDateSelectorControl } from "../../../../../../components/AgendaFilters/components/DateSelectorControl/AgendaDateSelectorControl";
import { AgendaWaitlist } from "../../../../../../components/AgendaWaitlist";
import { agendaMemberNotes_atom } from "../../../../../../features/MemberNotes/atoms";
import { ClubStackNavigatorParamList } from "../../../../../../navigators/ClubNavigator";
import { selectedCoursesByFacility } from "../../../../../../screens/AgendaScreen/AgendaScreens/MultiSlotBookingScreen/atoms/selectedCoursesByFacility";
import { useFlags } from "../../../../../../utils/launchDarkly";
import { MultiSlotSidebarFragment_facility$key } from "./__generated__/MultiSlotSidebarFragment_facility.graphql";
import { MultiSlotSidebarSessionFragment_session$key } from "./__generated__/MultiSlotSidebarSessionFragment_session.graphql";
import { MultiSlotFiltersWrapper } from "./components/MultiSlotFiltersWrapper";

type MultiSlotSidebarProps = {
  facilityRef: MultiSlotSidebarFragment_facility$key | null;
  sessionRef: MultiSlotSidebarSessionFragment_session$key | null;
  onJoinWaitlist: (queueSlotId: string, requestedTime: string | null) => void;
};

export const MultiSlotSidebar = ({
  facilityRef,
  sessionRef,
  onJoinWaitlist,
}: MultiSlotSidebarProps) => {
  const facility = useFragment(MultiSlotSidebarFragmentString, facilityRef);
  const session = useFragment(
    MultiSlotSidebarSessionFragmentString,
    sessionRef
  );
  const { memAppWaitlist: memAppWaitlistFeatureFlag } = useFlags();

  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();

  const route = useRoute<RouteProp<ClubStackNavigatorParamList, "Agenda">>();
  const currentlySelectedDate = route.params?.date;

  const date = route?.params?.date;

  const curDate = new Date();
  const todayString = format(curDate, "yyyy-MM-dd");
  const memberNote = useAtomValue(agendaMemberNotes_atom);

  const checkedCourses = useAtomValue(
    selectedCoursesByFacility(facility?.id ?? "")
  );

  const [currentCheckedCourses, setCurrentCheckedCourses] = useState<Record<
    string,
    boolean
  > | null>(checkedCourses);

  /**
   * Allows "today" as a string to be passed as the date and correctly converted
   */
  useEffect(() => {
    if (date === "today") {
      navigation.setParams({ date: todayString });
    }
  }, [date, navigation, todayString]);

  const navigateToSchedule = useScheduleNavigator();

  const onScheduleSwitcherChange = (option: ScheduleOption) => {
    const navigateDate = date ? date : todayString;
    navigateToSchedule(option.type, option.slug, navigateDate);
  };

  const selectedScheduleOption = facilityToScheduleOption(facility ?? null);

  return (
    <VStack gap={6}>
      {memberNote ? (
        <CourseNote
          header={memberNote.header}
          body={memberNote.body}
          priority={memberNote.status}
        />
      ) : null}
      <VStack gap={6} px={2}>
        <ScheduleSwitcher
          selectedOption={selectedScheduleOption}
          sessionRef={session ?? null}
          onChange={onScheduleSwitcherChange}
        />

        <AgendaDateSelectorControl
          facilityRef={facility ?? null}
          selectedDate={currentlySelectedDate ?? ""}
        />
        <MultiSlotFiltersWrapper
          facilityId={facility?.id ?? ""}
          currentCheckedCourses={currentCheckedCourses}
          setCurrentCheckedCourses={setCurrentCheckedCourses}
        />
      </VStack>
      {memAppWaitlistFeatureFlag ? (
        <AgendaWaitlist
          date={date || ""}
          facilityId={facility?.id ?? ""}
          courseIds={[]}
          onJoinWaitlist={onJoinWaitlist}
        />
      ) : null}
    </VStack>
  );
};

export const MultiSlotSidebarFragmentString = graphql`
  fragment MultiSlotSidebarFragment_facility on Facility {
    id
    slug
    name
    type
    ...AgendaDateSelectorControlFragment_facility
    ...facilityToScheduleOption_facility
  }
`;

const MultiSlotSidebarSessionFragmentString = graphql`
  fragment MultiSlotSidebarSessionFragment_session on Session {
    ...ScheduleSwitcher_session
  }
`;
